/**
 * Les jetons du thème — les valeurs qu'on CHOISIT, isolées de celles qu'on hérite.
 *
 * 🔴 CE FICHIER EXISTE PARCE QUE L'ANCIEN THÈME NE DISTINGUAIT PAS LES DEUX.
 * Il portait ~40 valeurs calculées (`spacing: 7.2`, `padding: '7.2px 21.6px'`,
 * `height: '24.3px'`, `fontSize: '1.0125rem'`…) issues d'un multiplicateur ×0,9
 * appliqué « pour ressembler à 90 % de zoom ». Mesuré au lot RU1 : **ce n'était
 * pas une échelle**. Une échelle multiplie tout par le même facteur ; celle-là
 * rendait la puce à 76 %, la ligne à 82 % — et le bouton à **110 %**, parce que
 * le ×0,9 avait été appliqué à `8px 24px` alors que le défaut réel de MUI est
 * `6px 16px`. Le multiplicateur portait sur une valeur qui n'était pas la
 * valeur de départ.
 */

// ─────────────────────────────────────────────────────────────────────────────
// DENSITÉ
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 🔴 TOUTE LA DENSITÉ DE L'APPLICATION TIENT DANS CE SEUL NOMBRE.
 *
 * Mesuré au lot RU1 dans un vrai moteur de rendu, sur une zone de liste de
 * 900 px :
 *
 *     MUI par défaut (padding 16px)  → ligne 65 px → 13 lignes
 *     ce jeton       (padding 10px)  → ligne 53 px → 16 lignes
 *
 * L'arithmétique est exacte : **65 − 53 = 12 = 2 × 6 px** de padding retiré.
 * Rien d'autre n'y contribue.
 *
 * ⚠️ NE « RAFFINEZ » PAS LA TYPOGRAPHIE EN CROYANT GAGNER DES LIGNES. C'est ce
 * qu'avait fait l'ancien thème, et c'est mesuré comme sans effet : la hauteur
 * d'une rangée est fixée par le PLUS GRAND de ses éléments — la puce (32 px) ou
 * le bouton (28 px) — pas par le texte. Réduire le corps de 16 à 14 px ne
 * change pas une rangée d'un pixel tant que la puce reste à 32. L'ancien thème
 * rapetissait puce ET texte pour arriver au même nombre de lignes, en abîmant
 * la lisibilité et les cibles tactiles au passage.
 */
export const PADDING_CELLULE_Y = 10;

/**
 * La contrepartie mobile, et elle va dans l'AUTRE SENS.
 *
 * 🔴 L'ancien `index.css` faisait l'inverse : `html { font-size: 90% }` sous
 * 768 px — qui se **composait** avec le ×0,9 du thème, soit une échelle réelle
 * de **0,81** — plus des boutons à `7px 14px !important`. Mesuré : le bouton
 * passait de 94,4 px de large sur bureau à **79,2 px sur mobile (−16 %)**, pour
 * une hauteur de **39,2 px, soit 4,8 px SOUS le seuil tactile** de 44 px.
 *
 * ⚠️ Or l'application est une PWA `standalone` avec scanner de code-barres et
 * capture photo : **c'est au doigt qu'elle sert le plus**. La densité y coûtait
 * exactement là où elle était le moins souhaitable.
 */
export const PADDING_CELLULE_Y_MOBILE = 16;

// ─────────────────────────────────────────────────────────────────────────────
// FORMES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ DEUX RAYONS, ET C'EST UN CHOIX, PAS UNE INCOHÉRENCE.
 *
 * L'ancien thème déclarait `shape.borderRadius: 12` puis le **contredisait dans
 * tous ses overrides** : 7.2 pour les boutons, champs et puces, 14.4 pour les
 * cartes et surfaces. La valeur globale n'était donc appliquée nulle part —
 * elle décrivait une intention que le reste du fichier annulait.
 *
 * Une valeur unique ne marche pas : à 12, un bouton devient une gélule ; à 8,
 * une carte paraît coupée au massicot. La convention retenue — **les surfaces
 * sont plus arrondies que les commandes qu'elles contiennent** — est courante
 * et se lit. On garde donc deux valeurs, **nommées et entières**, au lieu de
 * deux valeurs contradictoires dont l'une est décimale.
 */
export const RAYON_COMMANDE = 8;   // boutons, champs, puces
export const RAYON_SURFACE = 12;   // cartes, papiers, dialogues

// ─────────────────────────────────────────────────────────────────────────────
// MARQUE
// ─────────────────────────────────────────────────────────────────────────────

/** Les deux couleurs de marque. Elles alimentent la palette ET le dégradé. */
export const MARQUE_DEBUT = '#667eea';
export const MARQUE_FIN = '#764ba2';

/**
 * 🔴 LE DÉGRADÉ DE MARQUE — UN SEUL ENDROIT.
 *
 * Il était recopié **à l'identique dans 6 fichiers** (`Sidebar`, `Header`,
 * `SuperAdminShell`, `Login`, `Admin`, `Dashboard`), avec ses deux couleurs en
 * dur, alors que le thème déclarait déjà ces mêmes couleurs en `primary.main`
 * et `secondary.main`. Ce n'était pas une divergence de conception : c'était la
 * même valeur écrite sept fois.
 *
 * ⚠️ C'est une FONCTION, pas une constante, parce que le dégradé de survol et
 * le dégradé d'une couleur de stock choisie par l'utilisateur suivent la même
 * forme (`135deg`, 0 % → 100 %). Une constante aurait obligé à recopier la
 * forme pour la moindre variante — ce qui est exactement comment on repart à
 * sept copies.
 */
export const degrade = (debut = MARQUE_DEBUT, fin = MARQUE_FIN) =>
  `linear-gradient(135deg, ${debut} 0%, ${fin} 100%)`;

/**
 * Le dégradé VERTICAL des surfaces — barre latérale, panneaux.
 *
 * ⚠️ Forme distincte (`180deg`), donc jeton distinct : le faire passer par
 * `degrade()` aurait obligé à y ajouter un paramètre d'angle, c'est-à-dire à
 * transformer un jeton en petit moteur de dégradés. Deux formes, deux noms.
 * Ses couleurs, elles, viennent de la PALETTE et non de la marque : une
 * surface doit suivre le mode clair/sombre, pas la couleur de l'entreprise.
 */
export const degradeVertical = (debut, fin) =>
  `linear-gradient(180deg, ${debut} 0%, ${fin} 100%)`;

/** La variante survol : les deux couleurs assombries, forme identique. */
export const DEGRADE_SURVOL = degrade('#5a6fd8', '#6a4190');

/**
 * L'ombre TEINTÉE de marque, pour ce qui « flotte » au-dessus du fond.
 *
 * ⚠️ Ce n'est pas une ombre neutre : `rgba(102, 126, 234, …)` est
 * `MARQUE_DEBUT` en composantes décimales — la même couleur, écrite dans une
 * autre notation, donc invisible à une recherche sur `#667eea`. Elle était
 * recopiée dans 3 fichiers sous 3 opacités différentes.
 */
export const ombreMarque = (flou, opacite) =>
  `0 ${flou / 2}px ${flou}px rgba(102, 126, 234, ${opacite})`;

/*
 * 🔴 `DEGRADE_CONNEXION` ET `DEGRADE_DESACTIVE` ONT ÉTÉ RETIRÉS (RU7-d).
 *
 * Le premier était le fond violet plein écran du Login — le « trop démo » que
 * le propriétaire a relevé dès la toute première recette. Le second habillait
 * l'état désactivé du bouton de connexion, qui n'est plus un bouton à part.
 * Une fois le Login rendu à un fond neutre et à `variant="contained"`, les deux
 * n'avaient plus AUCUN appelant — mesuré, pas supposé.
 *
 * ⚠️ ON NE GARDE PAS UN JETON « AU CAS OÙ ». Un dégradé sans emploi est une
 * invitation à le remettre : le prochain écran qui cherche à se distinguer le
 * trouvera tout écrit, avec l'autorité d'un jeton du thème. *Ce qui n'a plus
 * d'appelant ne se garde pas, il se relit dans l'historique.*
 */

/**
 * La console SUPER_ADMIN — ardoise, délibérément HORS de la marque.
 *
 * ⚠️ Sa couleur dit « vous n'êtes plus dans le produit du client ». C'est une
 * information, pas une décoration : la faire suivre la marque effacerait la
 * frontière entre l'espace client et l'espace d'administration.
 */
export const DEGRADE_CONSOLE_SA = degrade('#37474f', '#263238');

// ─────────────────────────────────────────────────────────────────────────────
// TYPOGRAPHIE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 🔴 UNE SEULE PILE, ET C'EST LE NAVIGATEUR QUI ARBITRE, GLYPHE PAR GLYPHE.
 *
 * L'ancien thème déclarait `"Inter", "Roboto", …` alors qu'**Inter n'était
 * chargée par aucun chemin** : ni `@font-face`, ni paquet, ni fichier dans le
 * dépôt. `index.html` chargeait **Roboto** depuis Google Fonts. Le thème
 * annonçait donc une police et en affichait une autre, sur les 40 écrans,
 * depuis toujours.
 *
 * ⚠️ ET INTER N'A AUCUN GLYPHE HÉBREU — vérifié, ses `unicode-range` couvrent
 * latin, grec, cyrillique et vietnamien, **zéro occurrence de U+0590–05FF**.
 * L'application est trilingue `fr / en / he` : une pile latine seule aurait
 * laissé l'hébreu retomber sur une police système que personne n'a choisie,
 * c'est-à-dire le défaut d'origine déplacé sur la langue qui compte le plus
 * pour ce marché.
 *
 * **Noto Sans Hebrew** est retenue parce qu'elle est la plus proche d'Inter :
 * corps hébreu mesuré à 60,3 contre 57,1 (Rubik) et 56,1 (Assistant), pour une
 * fourchette Inter de 54,6 (hauteur d'x) à 72,8 (capitale). C'est l'impression
 * au changement de langue qui décide, pas les 3 Ko d'écart.
 *
 * ⚠️ AUCUN BASCULEMENT CONDITIONNÉ À LA LANGUE, et c'est mieux ainsi : une
 * phrase qui mêle les deux écritures — « commande n° 9 — מחסן ראשי » — est
 * rendue correctement par la pile, et le serait mal par un `if`.
 *
 * ⚠️ LES DEUX EN VARIABLE, pour couvrir 400/500/600/700 par de VRAIS dessins.
 * Si l'hébraïque ne fournissait qu'une graisse, le navigateur synthétiserait
 * les autres — un gras artificiel, visiblement plus laid que le vrai.
 */
/**
 * La pile de CHASSE FIXE — celle des code-barres, et de rien d'autre pour
 * l'instant.
 *
 * 🔴 ELLE EXISTE PARCE QU'ELLE ÉTAIT DÉJÀ RECOPIÉE TROIS FOIS, avec DEUX
 * valeurs différentes : `ui-monospace, Menlo, Consolas, monospace` au tableau
 * des produits (lot RU3-a) et le générique `monospace` NU dans deux champs
 * écrits plus tard. Le générique nu n'est pas équivalent — Chrome lui applique
 * sa « taille de police à chasse fixe » par défaut (13 px) au lieu d'hériter
 * de celle du contexte, donc le même code-barres n'a pas le même corps selon
 * l'écran qui le montre.
 *
 * ⚠️ `ui-monospace` D'ABORD : c'est la chasse fixe de l'INTERFACE du système
 * (SF Mono sur macOS, Cascadia sur Windows), pas une police de terminal. Un
 * code-barres se lit dans un formulaire, pas dans une console.
 */
export const PILE_CHASSE_FIXE = [
  'ui-monospace',
  'Menlo',
  'Consolas',
  'monospace',
].join(', ');

export const PILE_POLICES = [
  '"Inter Variable"',
  '"Noto Sans Hebrew Variable"',
  'system-ui',
  '-apple-system',
  '"Segoe UI"',
  'Roboto',
  'Helvetica',
  'Arial',
  'sans-serif',
].join(', ');
